import React, {
	useEffect,
	useId,
	useRef,
	useState,
	type ComponentProps,
	type ReactNode,
} from 'react';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import { Popup } from '@atlaskit/popup/popup';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';

import { CreditsSurface } from '../../internal/credits-surface';
import type { CreditsBannerProps } from '../credits-banner';

const styles = cssMap({
	content: {
		boxSizing: 'border-box',
		width: '248px',
		minHeight: '116px',
		maxWidth: '100%',
		textAlign: 'left',
	},
	hoverContent: { width: '320px' },
});

export type CreditsPopupTriggerProps = Parameters<ComponentProps<typeof Popup>['trigger']>[0] & {
	id: string;
	isSelected: boolean;
	onClick: () => void;
};

export type CreditsPopupProps = Omit<CreditsBannerProps, 'xcss'> & {
	/** Open on hover and focus instead of requiring a click. Defaults to `false`. */
	openOnHover?: boolean;
	/** Optional localized heading displayed inside the popup. */
	title?: string;
	/** Render a custom button, forwarding all supplied props (including ref) to it. */
	trigger?: (props: CreditsPopupTriggerProps) => ReactNode;
	/** Popup position relative to the trigger. Defaults to `bottom-start`. */
	placement?: ComponentProps<typeof Popup>['placement'];
	/** Popup `[skidding, distance]` offset from the trigger, in pixels. */
	offset?: ComponentProps<typeof Popup>['offset'];
	/**
	 * Render the popup next to its trigger instead of in a portal. Defaults to `true`.
	 * Set to `false` inside scrolling containers such as menus, which otherwise clip the popup and
	 * flip it to the opposite side.
	 */
	shouldRenderToParent?: boolean;
};

const CreditsPopupHoverWrapper = ({
	children,
	triggerId,
	contentRef,
	onOpenChange,
}: {
	children: ReactNode;
	triggerId: string;
	contentRef: React.RefObject<HTMLDivElement>;
	onOpenChange: (isOpen: boolean) => void;
}): React.JSX.Element => {
	const closeTimer = useRef<ReturnType<typeof setTimeout>>();
	const open = () => {
		clearTimeout(closeTimer.current);
		onOpenChange(true);
	};
	const close = () => {
		clearTimeout(closeTimer.current);
		onOpenChange(false);
	};
	useEffect(() => () => clearTimeout(closeTimer.current), []);

	return (
		<Box
			onMouseEnter={open}
			onMouseLeave={() => {
				closeTimer.current = setTimeout(() => {
					const activeElement = contentRef.current?.ownerDocument.activeElement ?? null;
					if (activeElement?.id !== triggerId && !contentRef.current?.contains(activeElement)) {
						close();
					}
				}, 150);
			}}
			onFocusCapture={(event: React.FocusEvent<HTMLDivElement>) => {
				if (!contentRef.current?.contains(event.relatedTarget)) {
					open();
				}
			}}
			onBlurCapture={(event: React.FocusEvent<HTMLDivElement>) => {
				if (
					!event.currentTarget.contains(event.relatedTarget) &&
					!contentRef.current?.contains(event.relatedTarget)
				) {
					close();
				}
			}}
		>
			{children}
		</Box>
	);
};

const CreditsPopupContent = ({
	title,
	triggerLabel,
	trigger,
	details,
	placement = 'bottom-start',
	offset,
	shouldRenderToParent = true,
	openOnHover = false,
}: {
	title?: string;
	triggerLabel: string;
	trigger?: CreditsPopupProps['trigger'];
	details: ReactNode;
	placement?: CreditsPopupProps['placement'];
	offset?: CreditsPopupProps['offset'];
	shouldRenderToParent?: boolean;
	openOnHover?: boolean;
}): React.JSX.Element => {
	const [isOpen, setIsOpen] = useState(false);
	const id = useId();
	const titleId = useId();
	const triggerId = useId();
	const contentRef = useRef<HTMLDivElement>(null);

	const popup = (
		<Popup
			id={id}
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			role={openOnHover ? undefined : 'dialog'}
			autoFocus={!openOnHover}
			shouldReturnFocus={!openOnHover}
			mode={openOnHover ? 'manual' : 'auto'}
			titleId={title ? titleId : triggerId}
			placement={placement}
			offset={offset}
			shouldFitViewport
			shouldRenderToParent={shouldRenderToParent}
			// Hosts such as menus may stop click propagation, which would hide outside clicks.
			shouldUseCaptureOnOutsideClick
			content={() => (
				<Box
					ref={contentRef}
					id={openOnHover ? `${id}-content` : undefined}
					role={openOnHover ? 'group' : undefined}
					aria-labelledby={openOnHover ? (title ? titleId : triggerId) : undefined}
					paddingBlock="space.250"
					paddingInline={openOnHover ? 'space.250' : 'space.400'}
					xcss={cx(styles.content, openOnHover && styles.hoverContent)}
				>
					<Stack space="space.100">
						{title && (
							<Heading size="small" as="h2" id={titleId}>
								{title}
							</Heading>
						)}
						{details}
					</Stack>
				</Box>
			)}
			trigger={(triggerProps) => {
				const buttonProps: CreditsPopupTriggerProps = {
					...triggerProps,
					id: triggerId,
					isSelected: isOpen,
					...(openOnHover && {
						'aria-haspopup': undefined,
						'aria-controls': isOpen ? `${id}-content` : undefined,
					}),
					onClick: () => setIsOpen((open) => openOnHover || !open),
				};
				return trigger ? (
					trigger(buttonProps)
				) : (
					<IconButton
						{...buttonProps}
						appearance="subtle"
						icon={(iconProps) => <InformationCircleIcon {...iconProps} size="medium" />}
						label={triggerLabel}
					/>
				);
			}}
		/>
	);
	return openOnHover ? (
		<CreditsPopupHoverWrapper
			triggerId={triggerId}
			contentRef={contentRef}
			onOpenChange={setIsOpen}
		>
			{popup}
		</CreditsPopupHoverWrapper>
	) : (
		popup
	);
};

export const CreditsPopup = ({
	title,
	trigger,
	placement,
	offset,
	shouldRenderToParent,
	openOnHover,
	...props
}: CreditsPopupProps): React.JSX.Element => (
	<CreditsSurface {...props}>
		{({ title: triggerLabel, description }) => (
			<CreditsPopupContent
				title={title}
				trigger={trigger}
				placement={placement}
				offset={offset}
				shouldRenderToParent={shouldRenderToParent}
				openOnHover={openOnHover}
				triggerLabel={triggerLabel}
				details={description}
			/>
		)}
	</CreditsSurface>
);
